import {view} from '@sail/ui';
import {
  Conversation,
  ConversationParticipant,
} from '@stripe-internal/conversations-client';
import {forwardRef, useEffect, useState} from 'react';
import {Subscription} from 'rxjs';
import {animate, AnimatePresence} from '@sail/engine';
import {TypingRow} from '../Chat/Row/TypingRow';
import {TypingRowDotCom} from '../Chat/Row/TypingRowDotCom';
import {NamedAvatar} from '../Chat/NamedAvatar';
import {
  ANIMATION_DURATION,
  StripeAvatar,
  useUniversalHelpDotComDesign,
} from '../index';

export const TypingIndicator = forwardRef(
  (
    props: {
      conversation: Conversation;
      animated?: boolean;
    },
    ref?: React.Ref<HTMLDivElement>,
  ) => {
    const {conversation, animated} = props;
    const [typers, setTypers] = useState<ConversationParticipant[]>([]);
    const useDotComDesign = useUniversalHelpDotComDesign();

    useEffect(() => {
      const sub = new Subscription();
      sub.add(conversation.typingObservable().subscribe(setTypers));
      return () => sub.unsubscribe();
    }, [conversation]);

    const typingRows = typers
      .filter((typer) => typer.id !== conversation.user.id)
      .map((typer) => {
        if (useDotComDesign) {
          return <TypingRowDotCom key={typer.displayName} />;
        }

        let avatar = null;
        if (typer.role === 'agent') {
          avatar = <NamedAvatar name={typer.displayName} />;
        } else if (typer.role === 'bot') {
          avatar = <StripeAvatar />;
        }

        return <TypingRow avatar={avatar} key={typer.displayName} />;
      });

    const hidden = typingRows.length < 1;

    return (
      <AnimatePresence>
        {hidden ? null : (
          <view.div
            ref={ref}
            style={{flexGrow: 0}}
            data-testid="typing-indicator"
            // Since this is just a typing indicator, we're setting the `aria-live` attribute to `off` so that
            // screen readers don't interrupt theirselves when new messages come in.
            aria-live="off"
            uses={
              animated
                ? [
                    animate.out({
                      duration: useDotComDesign ? 0 : ANIMATION_DURATION,
                      opacity: 0,
                      transform: 'translateY(calc(120% * 1))',
                      easing: 'ease-in',
                    }),
                    animate.in({
                      duration: ANIMATION_DURATION,
                      transform: 'translateY(calc(100% * 1))',
                      easing: 'ease-out',
                    }),
                  ]
                : []
            }
          >
            {typingRows}
          </view.div>
        )}
      </AnimatePresence>
    );
  },
);
